import { useRef, useEffect, useState } from "react";
import { Link as RemixLink, useFetcher } from "@remix-run/react";
import {
  Text,
  Button,
  Link,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  AlertDialogCloseButton,
  useDisclosure,
  VStack,
  HStack,
  Box,
  Skeleton,
  Wrap,
  Stack,
} from "@chakra-ui/react";

import { errors } from "#app/utils/constants/index.js";
import { maybePluralizeNoun } from "#app/utils/words.js";
import { useUserAvailabilityRequestLimit } from "#app/hooks/index.js";
import { PauseIcon } from "#app/components/Icons.jsx";
import { Paper } from "#app/components/index.jsx";
import {
  AvailabilityRequestArrivalBadge,
  AvailabilityRequestNightsBadge,
  AvailabilityRequestSitesBadge,
  AvailabilityRequestCapacityBadge,
} from "#app/components/index.jsx";
import { partitionPauseableActive } from "#app/models/freeAccessQualifying.js";

function ActiveRequestItem({ request, onPause, isLoading, disabled }) {
  return (
    <Stack
      as={Paper}
      spacing={{ base: "4", md: "4" }}
      direction={{ base: "column", md: "row" }}
      justify="space-between"
      alignItems={{ base: "flex-start", md: "center" }}
    >
      <Box w="100%">
        <Box
          display={{ base: "flex", md: "flex" }}
          alignItems={{ base: "flex-start", md: "center" }}
          justifyContent={{ base: "space-between", md: "flex-start" }}
        >
          <Text as="h2" fontSize="md" fontWeight="bold" letterSpacing="normal">
            {request.facility.name}
          </Text>
        </Box>
        <Text color="subtle" fontSize="sm">
          {request.facility.subName}
        </Text>
        <AvailabilityRequestArrivalBadge
          availabilityRequest={request}
          fontSize="xs"
        />
        <Wrap spacingX={2} spacingY="0px">
          <AvailabilityRequestNightsBadge
            availabilityRequest={request}
            mr={1}
            fontSize="xs"
          />
          <AvailabilityRequestSitesBadge
            availabilityRequest={request}
            mr={1}
            fontSize="xs"
          />
          <AvailabilityRequestCapacityBadge
            availabilityRequest={request}
            fontSize="xs"
          />
        </Wrap>
      </Box>
      <Stack direction={{ base: "row", md: "column" }}>
        <Button
          variant="outline"
          size="sm"
          colorScheme="orange"
          onClick={() => onPause(request)}
          isLoading={isLoading}
          isDisabled={disabled}
          loadingText="Pausing..."
          leftIcon={<PauseIcon />}
        >
          Pause
        </Button>
      </Stack>
    </Stack>
  );
}

function ActiveRequestItemSkeleton() {
  return (
    <Stack
      as={Paper}
      spacing={{ base: "4", md: "4" }}
      direction={{ base: "column", md: "row" }}
      justify="space-between"
      alignItems={{ base: "flex-start", md: "center" }}
    >
      <Box w="100%">
        <Skeleton height="24px" width="60%" mb={2} />
        <Skeleton height="16px" width="40%" mb={2} />
        <Skeleton height="20px" width="30%" mb={2} />
        <HStack spacing={2}>
          <Skeleton height="18px" width="60px" />
          <Skeleton height="18px" width="50px" />
          <Skeleton height="18px" width="40px" />
        </HStack>
      </Box>
      <Stack direction={{ base: "row", md: "column" }}>
        <Skeleton height="28px" width="65px" borderRadius="md" />
      </Stack>
    </Stack>
  );
}

export function AvailabilityRequestLimitModal({
  fetcher,
  actionText = "activate",
  defaultIsOpen = false,
  forceReload = false,
  // Optional external useDisclosure() — lets a caller (e.g. the request-form
  // limit banner) open the modal proactively instead of waiting for a
  // limit-reached submit error.
  disclosure,
}) {
  const { limit, canSubscribe } = useUserAvailabilityRequestLimit();
  const internalDisclosure = useDisclosure({ defaultIsOpen });
  const {
    isOpen,
    onOpen,
    onClose: originalOnClose,
  } = disclosure ?? internalDisclosure;

  const [showPauseOptions, setShowPauseOptions] = useState(false);
  const [pausingRequestId, setPausingRequestId] = useState(null);

  const pauseFetcher = useFetcher();
  const activeRequestsFetcher = useFetcher();

  useEffect(() => {
    if (
      fetcher &&
      fetcher.state === "idle" &&
      fetcher.data != null &&
      fetcher.data?.error === errors.AVAILABILITY_REQUEST_LIMIT_REACHED
    ) {
      onOpen();
    }
  }, [fetcher, onOpen]);

  useEffect(() => {
    if (isOpen) {
      activeRequestsFetcher.load("/api/browse");
    }
  }, [isOpen]);

  useEffect(() => {
    if (pauseFetcher.state === "idle" && pauseFetcher.data?.ok) {
      originalOnClose();
    }
  }, [pauseFetcher.state, pauseFetcher.data, originalOnClose]);

  const handlePauseRequest = (request) => {
    setPausingRequestId(request.id);
    pauseFetcher.submit(
      { _status: "paused", _context: "limit-modal" },
      { method: "POST", action: `/r/${request.uuid}?index` },
    );
  };

  const handleShowPauseOptions = () => {
    setShowPauseOptions(true);
  };

  const handleBackToMainView = () => {
    setShowPauseOptions(false);
  };

  if (!limit) {
    return null;
  }

  // Free-access requests don't count toward the limit, so pausing one
  // doesn't free up a slot. Only show requests the user can actually trade
  // in; freeAccessCount drives the empty-state explanation.
  const { pauseable: pauseableRequests, freeAccessCount } =
    partitionPauseableActive(activeRequestsFetcher.data?.active || []);
  const isLoadingRequests = activeRequestsFetcher.state === "loading";

  return (
    <AlertDialog isOpen={isOpen} onClose={originalOnClose}>
      <AlertDialogOverlay data-testid="AvailabilityRequestLimitModal">
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {showPauseOptions
              ? "Choose a request to pause"
              : "You've reached your limit"}
          </AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            {!showPauseOptions ? (
              <>
                {canSubscribe ? (
                  <Text mb={2}>
                    Your account is limited to{" "}
                    <strong>
                      {maybePluralizeNoun(limit, "active request")}
                    </strong>
                    .
                  </Text>
                ) : (
                  <Text mb={2}>
                    To encourage fair use of the app, your account is limited to{" "}
                    <strong>
                      {maybePluralizeNoun(limit, "active request")}
                    </strong>
                    .
                  </Text>
                )}
                <Text mb={4}>
                  To {actionText} this request, you can pause an existing one
                  {canSubscribe && (
                    <>
                      {" "}
                      or{" "}
                      <Link as={RemixLink} to="/subscribe">
                        upgrade your account
                      </Link>
                    </>
                  )}
                  .
                </Text>
                {!canSubscribe && (
                  <Text fontSize="sm" color="muted">
                    Want more requests? Email us at{" "}
                    <Link
                      isExternal
                      href="mailto:help@schnerp.com?subject=Need%20more%20requests"
                    >
                      help@schnerp.com
                    </Link>{" "}
                    with your situation and we'll try to help you out.
                  </Text>
                )}
              </>
            ) : (
              <VStack align="stretch" spacing={4}>
                <Text>
                  Paused requests don't count towards your limit and can be
                  reactivated anytime.
                </Text>
                {isLoadingRequests ? (
                  <VStack
                    align="stretch"
                    spacing={2}
                    maxH="300px"
                    overflowY="auto"
                  >
                    <ActiveRequestItemSkeleton />
                  </VStack>
                ) : activeRequestsFetcher.data === undefined ? (
                  <Box textAlign="center" py={4}>
                    <Text color="muted">No data loaded yet...</Text>
                  </Box>
                ) : pauseableRequests.length === 0 ? (
                  <Box textAlign="center" py={4}>
                    <Text color="muted">
                      No requests counting toward your limit
                    </Text>
                    {freeAccessCount > 0 && (
                      <Text fontSize="sm" color="muted" mt={1}>
                        Free requests don&rsquo;t consume a slot, so pausing one
                        wouldn&rsquo;t free space for a new request.
                      </Text>
                    )}
                  </Box>
                ) : (
                  <VStack
                    align="stretch"
                    spacing={2}
                    maxH="300px"
                    overflowY="auto"
                  >
                    {pauseableRequests.map((request) => (
                      <ActiveRequestItem
                        key={request.id}
                        request={request}
                        onPause={handlePauseRequest}
                        isLoading={pausingRequestId === request.id}
                        disabled={pausingRequestId !== null}
                      />
                    ))}
                  </VStack>
                )}
              </VStack>
            )}
          </AlertDialogBody>
          <AlertDialogFooter>
            {!showPauseOptions ? (
              <>
                <Button onClick={handleShowPauseOptions} mr={3}>
                  Pause a request
                </Button>
                {canSubscribe && (
                  <Button
                    ml={2}
                    variant="primary"
                    onClick={originalOnClose}
                    as={forceReload ? Link : RemixLink}
                    to={forceReload ? undefined : "/subscribe"}
                    href={forceReload ? "/subscribe" : undefined}
                  >
                    Upgrade Account
                  </Button>
                )}
              </>
            ) : (
              <Button
                onClick={handleBackToMainView}
                mr={3}
                isDisabled={pausingRequestId !== null}
              >
                Back
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
